import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { NgClass } from '@angular/common';
import { ScheduleService } from '@services/schedules.service';
import {
  AtSameTime,
  OffsetAfterTimes,
  OffsetBeforeTimes,
  Schedule,
  SolarEventOptions,
  solarEventRequiredValidator
} from '@app/shared/models/schedule';
import { Rule } from '@app/shared/models/rule';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ScheduleRuleComponent } from '@app/schedules/schedule-rule/schedule-rule.component';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { DayOfTheWeek } from '@app/shared/models/day-of-the-week';
import { TimeUtils } from '@app/shared/utils/time.utils';
import { ConfirmationDialogService } from '@services/confirmation-dialog/confirmation-dialog.service';
import { ConfirmDialogData } from '@components/dialogs/confirm/confirm.component';
import { NotificationBlockComponent, StatusClass } from '@components/notification-block/notification-block.component';
import { IScheduleWeekdayForm } from '@app/shared/models/schedule-form.interface';
import { Building } from '@app/shared/models/building.interface';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { add, isBefore, isSameDay, sub } from 'date-fns';
import * as SunCalc from 'suncalc';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { TimezoneUtils } from '@app/shared/utils/timezoneUtils';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { ToastService } from '@services/toast/toast.service';

@Component({
  selector: 'app-form-weekday-schedule',
  standalone: true,
  imports: [
    FormsModule,
    MatButton,
    MatFormFieldModule,
    MatIcon,
    MatInput,
    MatSelectModule,
    MatTooltip,
    NgClass,
    NotificationBlockComponent,
    ReactiveFormsModule,
    ScheduleRuleComponent,
    MatSlideToggleModule,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle
  ],
  templateUrl: './form-weekday-schedule.component.html',
  styleUrl: './form-weekday-schedule.component.scss'
})
export class FormWeekdayScheduleComponent implements OnInit {
  @Input() schedule: Schedule = new Schedule();
  @Input() building: Building;
  @Output() onDeleteSchedule = new EventEmitter<number>();
  @Output() onSaveNewSchedule = new EventEmitter<number>();
  @Output() onClose = new EventEmitter<void>();
  @Output() onChange = new EventEmitter<Schedule>();
  isNew: boolean;
  saveLabel = 'Save';
  form: FormGroup<IScheduleWeekdayForm>;
  readonly StatusClass = StatusClass;
  solarEventOptions = SolarEventOptions;
  startDateTime: Date;
  endDateTime: Date;
  currentDate: Date;
  minSelectableStartDate: Date;
  offsetBeforeTimes = OffsetBeforeTimes;
  offsetAfterTimes = OffsetAfterTimes;
  atSameTime = AtSameTime;

  private timesInDay = TimeUtils.getTimesInDay(10);

  constructor(
    private scheduleService: ScheduleService,
    private fb: FormBuilder,
    private readonly dialog: ConfirmationDialogService,
    private readonly toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.timesInDay.push('23:59');
    if (!this.schedule?.id) {
      this.isNew = true;
    }
    this.setupDates();
    this.setForm();

    this.currentDate = TimeUtils.convertTimezone(
      new Date(),
      TimezoneUtils.mapUtcToKnownTimezone(this.building.timeZone)
    );
    this.minSelectableStartDate =
      this.isNew || this.schedule.startDate > this.currentDate ? this.currentDate : this.schedule.startDate;
  }

  setForm(): void {
    const allOffsetOptions = [this.atSameTime, ...this.offsetBeforeTimes, ...this.offsetAfterTimes];
    const selectedOffsetStart =
      allOffsetOptions.find(
        (opt) =>
          opt.time.value === this.schedule.startOffset?.value && opt.time.offset === this.schedule.startOffset?.offset
      ) || this.atSameTime;
    const selectedOffsetEnd =
      allOffsetOptions.find(
        (opt) =>
          opt.time.value === this.schedule.endOffset?.value && opt.time.offset === this.schedule.endOffset?.offset
      ) || this.atSameTime;
    this.form = this.fb.group(
      {
        name: [this.schedule.name || '', [Validators.required]],
        startDate: new FormControl(new Date(this.schedule.startDate.getTime()), {
          validators: [Validators.required],
          updateOn: 'blur'
        }),
        isStartSolarEvent: [!!this.schedule.startSolarEvent, Validators.required],
        isEndSolarEvent: [!!this.schedule.endSolarEvent, Validators.required],
        startTime: [
          { value: this.schedule?.startTime || '00:00', disabled: this.schedule?.alwaysActive },
          [Validators.required]
        ],
        endTime: [
          { value: this.schedule?.endTime || '00:00', disabled: this.schedule?.alwaysActive },
          [Validators.required]
        ],
        startSolarEvent: [this.schedule.startSolarEvent || null],
        endSolarEvent: [this.schedule.endSolarEvent || null],
        offsetStartTime: [selectedOffsetStart.time],
        offsetEndTime: [selectedOffsetEnd.time],
        days: new FormArray([new FormControl(false)])
      },
      { validators: solarEventRequiredValidator }
    );

    this.createDayFormArray();
  }

  createDayFormArray(): void {
    this.days.clear();
    this.daysOfTheWeek.forEach((dayOfTheWeek) => {
      const day = new FormControl(this.schedule?.days?.isActive(dayOfTheWeek.ordinal));
      this.days.push(day);
    });
  }

  setDays(index: number): void {
    const value = this.daysOfTheWeekControl[index].value;
    this.daysOfTheWeekControl[index].setValue(!value);
    this.schedule.days.toggleActive(index + 1);
    if (this.form.value.isEndSolarEvent || this.form.value.isStartSolarEvent) {
      this.setStartAndEndTime();
    }
  }

  get endTimeFormControl(): string {
    return this.form.get('endTime').getRawValue();
  }

  get startTimeFormControl(): string {
    return this.form.get('startTime').getRawValue();
  }

  get daysOfTheWeekControl(): FormControl[] {
    return this.days.controls as FormControl[];
  }

  get days(): FormArray {
    return this.form.get('days') as FormArray;
  }

  get daysOfTheWeek(): DayOfTheWeek[] {
    return DayOfTheWeek.all();
  }

  get times(): string[] {
    return this.timesInDay;
  }

  get startDate(): AbstractControl {
    return this.form.get('startDate');
  }

  get offsetStartTime(): AbstractControl {
    return this.form.get('offsetStartTime');
  }

  get offsetEndTime(): AbstractControl {
    return this.form.get('offsetEndTime');
  }

  get startSolarEvent(): AbstractControl {
    return this.form.get('startSolarEvent');
  }

  get endSolarEvent(): AbstractControl {
    return this.form.get('endSolarEvent');
  }

  close(): void {
    this.onClose.emit();
  }

  updateStartTime(): void {
    this.schedule.startTime = this.startTimeFormControl;
    this.onChange.emit(this.schedule);
  }

  updateEndTime(): void {
    this.schedule.endTime = this.endTimeFormControl;
    this.onChange.emit(this.schedule);
  }

  addRule(): void {
    this.schedule.rules?.push(new Rule());
  }

  removeRule(index: number): void {
    this.schedule.rules.splice(index, 1);
  }

  save(): void {
    if (this.form.invalid) {
      return;
    }

    this.saveLabel = 'Saving...';

    this.schedule.name = this.form.get('name').value;
    if (this.schedule.id != null) {
      this.scheduleService.updateSchedule(Schedule.toDto(this.schedule)).subscribe({
        next: (schedule) => {
          this.schedule = Schedule.clone(Schedule.fromDto(schedule));
          this.close();
          this.saveLabel = 'Save';
        },
        error: () => {
          this.toastService.error({ message: 'Error updating Schedules', dataCy: 'update-schedule-error-toast' });
        }
      });
    } else {
      this.scheduleService.saveSchedule(Schedule.toDto(this.schedule), this.building.id).subscribe({
        next: (schedule) => {
          this.schedule = Schedule.clone(schedule);
          this.onSaveNewSchedule.emit();
          this.saveLabel = 'Save';
        },
        error: () => {
          this.toastService.error({ message: 'Error saving Schedules', dataCy: 'save-schedule-error-toast' });
        }
      });
    }
  }

  delete(): void {
    const data = new ConfirmDialogData(`Delete schedule ${this.schedule.name}?`, 'Delete Schedule');
    this.dialog.open(data).subscribe({
      next: (result) => {
        if (result && this.schedule.id != null) {
          this.scheduleService.deleteSchedule(this.schedule).subscribe({
            next: () => {
              this.onDeleteSchedule.emit(this.schedule.id);
            }
          });
        } else {
          return;
        }
      }
    });
  }

  validateRules(): boolean {
    if (!this.schedule?.rules?.length) {
      return false;
    }

    return this.schedule.rules.every((rule) => {
      return rule.command != null && rule.value != null;
    });
  }

  get validateActiveWeekDays(): boolean {
    return this.daysOfTheWeekControl.some((day) => day.getRawValue() === true);
  }

  get validateWeekDayTimeSchedule(): boolean {
    return TimeUtils.isAfter(this.schedule.endTime, this.schedule.startTime);
  }

  get validateStartSolarEventSelected(): boolean {
    if (this.form.get('isStartSolarEvent').value) {
      return !!this.startSolarEvent.value;
    } else {
      return true;
    }
  }

  get validateEndSolarEventSelected(): boolean {
    if (this.form.get('isEndSolarEvent').value) {
      return !!this.endSolarEvent.value;
    } else {
      return true;
    }
  }

  onChangeStartDate(): void {
    this.schedule.startDate = this.startDate.value;
    if (this.form.value.isStartSolarEvent || this.form.value.isEndSolarEvent) {
      this.setStartAndEndTime();
    }
  }

  onStartSolarEventsToggle(): void {
    this.resetSolarEventControls(true);
  }

  onEndSolarEventsToggle(): void {
    this.resetSolarEventControls(false);
  }

  onChangeStartSolarEvent(): void {
    this.schedule.startSolarEvent = this.startSolarEvent.value;
    this.offsetStartTime.reset(this.atSameTime.time);
    this.schedule.startOffset = this.atSameTime.time;
    this.offsetEndTime.enable();
    this.setStartAndEndTime();
    if (this.schedule.startSolarEvent == 'SUNSET') {
      this.form.get('endSolarEvent').reset();
      this.schedule.endTime = '00:00';
    }
  }

  onChangeEndSolarEvent(): void {
    this.schedule.endSolarEvent = this.form.get('endSolarEvent').value;
    this.offsetEndTime.reset(this.atSameTime.time);
    this.schedule.endOffset = this.atSameTime.time;
    this.offsetEndTime.enable();
    this.setStartAndEndTime();
  }

  onChangeOffsetStartTime(): void {
    const offsetStartTime = this.offsetStartTime.value;
    this.updateOffsetTime(offsetStartTime.value, offsetStartTime.offset, this.startDateTime, true);
  }

  onChangeOffsetEndTime(): void {
    const offsetEndTime = this.offsetEndTime.value;
    this.updateOffsetTime(offsetEndTime.value, offsetEndTime.offset, this.endDateTime, false);
  }

  isCurrentDateActive(): boolean {
    const currentDayIndex = this.currentDate.getDay() === 0 ? 7 : this.currentDate.getDay();
    return this.schedule.days.isActive(currentDayIndex);
  }

  getSolarTimes(date?: Date): { sunrise: Date; sunset: Date } {
    const nextActiveDate = add(date, { hours: 4 });
    const astroTimes = SunCalc.getTimes(nextActiveDate, this.building.latitude, this.building.longitude);
    return {
      sunrise: astroTimes.sunrise,
      sunset: astroTimes.sunset
    };
  }

  setStartAndEndTime(): void {
    if (isSameDay(this.schedule.startDate, this.currentDate)) {
      this.handleTodayAsStartDate();
    } else {
      this.handlePastAndFutureStartDate();
    }
  }

  get startControlDirty(): boolean {
    return this.form.controls.startTime.dirty || this.form.controls.startSolarEvent.dirty;
  }

  get endControlsDirty(): boolean {
    return this.form.controls.endTime.dirty || this.form.controls.endSolarEvent.dirty;
  }

  private handleTodayAsStartDate(): void {
    if (this.isCurrentDateActive()) {
      this.setTimeFromSolarEvent(this.schedule.startDate);
    } else {
      this.findNextActiveDayAfterStartDateAndSetTime();
    }
  }

  private handlePastAndFutureStartDate(): void {
    let date = this.schedule.startDate;
    if (isBefore(this.schedule.startDate, this.currentDate)) {
      date = this.currentDate;
    }
    const startDateDayIndex = date.getDay() === 0 ? 7 : date.getDay();
    if (this.schedule.days.isActive(startDateDayIndex)) {
      this.setTimeFromSolarEvent(date);
    } else {
      this.findNextActiveDayAfterStartDateAndSetTime(date);
    }
  }

  private findNextActiveDayAfterStartDateAndSetTime(date?: Date): void {
    const dateToUse = date || this.schedule.startDate;
    const startDateDayIndex = this.getDayIndex(dateToUse);
    const nextActiveDay = this.schedule.days.getNextActiveDay(startDateDayIndex);
    const nextActiveDate = this.schedule.days.getDateFromDayIndex(nextActiveDay, dateToUse);

    this.setTimeFromSolarEvent(nextActiveDate);
  }

  setStartDateTime(date: Date): void {
    const [hours, minutes] = this.schedule.startTime.split(':').map(Number);
    this.startDateTime = new Date(date);
    this.startDateTime.setHours(hours, minutes, 0, 0);
  }

  setEndDateTime(date: Date): void {
    const [hours, minutes] = this.schedule.endTime.split(':').map(Number);
    this.endDateTime = new Date(date);
    this.endDateTime.setHours(hours, minutes, 0, 0);
  }

  private setTimeFromSolarEvent(date: Date): void {
    if (this.form.value.isStartSolarEvent) {
      this.schedule.startTime = this.getSolarEventTime(date, this.schedule.startSolarEvent).timeString;
      this.setStartDateTime(date);
      if (this.schedule.startOffset) {
        this.updateOffsetTime(
          this.schedule.startOffset.value,
          this.schedule.startOffset.offset,
          this.startDateTime,
          true
        );
      }
    }
    if (this.form.value.isEndSolarEvent) {
      this.schedule.endTime = this.getSolarEventTime(date, this.schedule.endSolarEvent).timeString;
      this.setEndDateTime(date);
      if (this.schedule.endOffset) {
        this.updateOffsetTime(this.schedule.endOffset.value, this.schedule.endOffset.offset, this.endDateTime, false);
      }
    }
  }

  private getSolarEventTime(localDate: Date, event: string): { hours: number; minutes: number; timeString: string } {
    const utcDate = fromZonedTime(localDate.setHours(0, 0, 0, 0), this.building.timeZone);
    const solarTimes = this.getSolarTimes(utcDate);
    const selectedTime = event === 'SUNRISE' ? solarTimes.sunrise : solarTimes.sunset;
    const selectedTimeInBuildingTZ = toZonedTime(selectedTime, this.building.timeZone);
    const [hours, minutes] = selectedTimeInBuildingTZ.toTimeString().slice(0, 5).split(':').map(Number);

    return { hours, minutes, timeString: selectedTimeInBuildingTZ.toTimeString().slice(0, 5) };
  }

  private updateOffsetTime(offsetTime: string, toggleValue: string, baseDateTime: Date, isStart: boolean): void {
    const updatedTime = toggleValue
      ? toggleValue === 'minus'
        ? sub(baseDateTime, { minutes: +offsetTime })
        : add(baseDateTime, { minutes: +offsetTime })
      : baseDateTime;

    const timeString = updatedTime.toTimeString().slice(0, 5);

    if (isStart) {
      this.schedule.startTime = timeString;
      this.schedule.startOffset = { value: offsetTime, offset: toggleValue };
    } else {
      this.schedule.endTime = timeString;
      this.schedule.endOffset = { value: offsetTime, offset: toggleValue };
    }
    this.onChange.emit(this.schedule);
  }

  private resetSolarEventControls(isStart: boolean): void {
    this.form.get(isStart ? 'startTime' : 'endTime').reset('00:00');
    this.form.get(isStart ? 'startSolarEvent' : 'endSolarEvent').reset(null);
    (isStart ? this.offsetStartTime : this.offsetEndTime).reset(this.atSameTime.time);

    if (isStart) {
      this.schedule.startTime = '00:00';
      this.schedule.startSolarEvent = null;
      this.schedule.startOffset = undefined;
    } else {
      this.schedule.endTime = '00:00';
      this.schedule.endSolarEvent = null;
      this.schedule.endOffset = undefined;
    }

    this.onChange.emit(this.schedule);
  }

  private getDayIndex(date: Date) {
    return date.getDay() === 0 ? 7 : date.getDay();
  }

  private setupDates() {
    const startDate = TimeUtils.convertTimezone(
      new Date(this.schedule.nextRunStartTime),
      TimezoneUtils.mapUtcToKnownTimezone(this.building.timeZone)
    );
    this.startDateTime = this.schedule.startOffset?.offset
      ? this.schedule.startOffset.offset === 'minus'
        ? add(startDate, { minutes: +this.schedule.startOffset.value })
        : sub(startDate, { minutes: +this.schedule.startOffset.value })
      : startDate;

    const endDate = TimeUtils.convertTimezone(
      new Date(this.schedule.nextRunEndTime),
      TimezoneUtils.mapUtcToKnownTimezone(this.building.timeZone)
    );

    this.endDateTime = this.schedule.endOffset?.offset
      ? this.schedule.endOffset.offset === 'minus'
        ? add(endDate, { minutes: +this.schedule.endOffset.value })
        : sub(endDate, { minutes: +this.schedule.endOffset.value })
      : endDate;
  }
}
